import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { AlertTriangle, X, ChevronRight, Bell } from 'lucide-react';

/**
 * HighPriorityBanner - Shows toast/banner for HIGH priority notifications
 * Appears on any page when user is in the app
 * Rate limited to avoid spam
 */
const HighPriorityBanner = ({ user }) => {
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [visible, setVisible] = useState(false);
  const lastShownRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastCheckRef = useRef(0); // Initialize to 0 so first check happens immediately

  // Check for high priority notifications
  const checkHighPriority = useCallback(async () => {
    if (!user) return;
    
    // Rate limit: Don't show same notification within 5 minutes
    const now = Date.now();
    const timeSinceLastCheck = now - lastCheckRef.current;
    
    // Only check every 30 seconds minimum
    if (timeSinceLastCheck < 30000) return;
    lastCheckRef.current = now;
    
    try {
      const response = await api.getHighPriorityNotifications(5); // Last 5 minutes
      const notifications = response.data?.notifications || [];
      
      if (notifications.length > 0) {
        // Get the most recent one that hasn't been shown
        const newNotification = notifications.find(n => 
          n.notification_id !== lastShownRef.current
        );
        
        if (newNotification) {
          lastShownRef.current = newNotification.notification_id;
          setNotification(newNotification);
          setVisible(true);
          
          // Auto-hide after 10 seconds
          setTimeout(() => {
            setVisible(false);
          }, 10000);
        }
      }
    } catch (error) {
      console.error('Failed to check high priority notifications:', error);
    }
  }, [user]);

  // Setup polling
  useEffect(() => {
    if (!user) return;
    
    // Initial check on mount
    checkHighPriority();
    
    // Poll every 60 seconds when in foreground, 3 minutes in background
    const setupPolling = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      
      const interval = document.visibilityState === 'visible' ? 60000 : 180000;
      pollIntervalRef.current = setInterval(checkHighPriority, interval);
    };
    
    setupPolling();
    
    // Listen for visibility changes
    const handleVisibilityChange = () => {
      setupPolling();
      if (document.visibilityState === 'visible') {
        checkHighPriority(); // Immediate check when coming back
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, checkHighPriority]);

  const handleClick = () => {
    if (notification?.deep_link) {
      navigate(notification.deep_link);
    } else {
      navigate('/notifications');
    }
    setVisible(false);
    
    // Mark as read
    if (notification?.notification_id) {
      api.markNotificationRead(notification.notification_id).catch(console.error);
    }
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    setVisible(false);
  };

  if (!visible || !notification) return null;

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[100] p-3 animate-slide-down"
      style={{
        animation: 'slideDown 0.3s ease-out'
      }}
    >
      <style>{`
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      
      <button
        onClick={handleClick}
        className="w-full bg-gradient-to-r from-red-600 to-red-500 text-white rounded-2xl p-4 shadow-lg flex items-center gap-3 hover:from-red-700 hover:to-red-600 transition-all"
        data-testid="high-priority-banner"
      >
        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
          <AlertTriangle size={20} />
        </div>
        
        <div className="flex-1 text-left min-w-0">
          <p className="font-bold text-sm">{notification.title}</p>
          <p className="text-xs text-white/80 line-clamp-1 mt-0.5">
            {notification.message}
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <ChevronRight size={18} className="text-white/60" />
          <button
            onClick={handleDismiss}
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </button>
    </div>
  );
};

export default HighPriorityBanner;
